import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import type { ReactNode } from "react";
import { defaultBranding, type BrandingTokens } from "./tokens";
import { accessibleBranding } from "./accessibility";

/** Compare deux jeux de tokens sur les clés de `defaultBranding`. */
function tokensEqual(a: BrandingTokens, b: BrandingTokens): boolean {
  for (const key of Object.keys(defaultBranding) as Array<keyof BrandingTokens>) {
    if (a[key] !== b[key]) return false;
  }
  return true;
}

interface BrandingContextValue {
  tokens: BrandingTokens;
  setTokens: (tokens: Partial<BrandingTokens>) => void;
  reset: () => void;
}

const BrandingContext = createContext<BrandingContextValue | null>(null);

function applyTokensToDocument(tokens: BrandingTokens) {
  const root = document.documentElement;
  root.style.setProperty("--brand", tokens.brand);
  root.style.setProperty("--brand-foreground", tokens.brandForeground);
  root.style.setProperty("--brand-hover", tokens.brandHover);
  root.style.setProperty("--brand-muted", tokens.brandMuted);
  root.style.setProperty("--accent", tokens.accent);
  root.style.setProperty("--accent-foreground", tokens.accentForeground);
  if (tokens.fontSans) root.style.setProperty("--font-sans", tokens.fontSans);
  if (tokens.fontDisplay) root.style.setProperty("--font-display", tokens.fontDisplay);
  if (tokens.faviconUrl) {
    let link = document.querySelector<HTMLLinkElement>('link[rel~="icon"]');
    if (!link) {
      link = document.createElement("link");
      link.rel = "icon";
      document.head.appendChild(link);
    }
    link.href = tokens.faviconUrl;
  }
}

interface BrandingProviderProps {
  children: ReactNode;
  initial?: Partial<BrandingTokens>;
}

export function BrandingProvider({ children, initial }: BrandingProviderProps) {
  const [tokens, setTokensState] = useState<BrandingTokens>({
    ...defaultBranding,
    ...initial,
  });
  const readableTokens = useMemo(() => accessibleBranding(tokens), [tokens]);

  useEffect(() => {
    applyTokensToDocument(readableTokens);
  }, [readableTokens]);

  // Callbacks stables : mises à jour fonctionnelles + bailout si rien ne change,
  // sinon `BrandingSync` boucle (Maximum update depth exceeded).
  const setTokens = useCallback((next: Partial<BrandingTokens>) => {
    setTokensState((prev) => {
      let changed = false;
      for (const key of Object.keys(next) as Array<keyof BrandingTokens>) {
        if (next[key] !== undefined && prev[key] !== next[key]) {
          changed = true;
          break;
        }
      }
      return changed ? { ...prev, ...next } : prev;
    });
  }, []);

  const reset = useCallback(() => {
    setTokensState((prev) => (tokensEqual(prev, defaultBranding) ? prev : { ...defaultBranding }));
  }, []);

  const value = useMemo<BrandingContextValue>(
    () => ({ tokens: readableTokens, setTokens, reset }),
    [readableTokens, setTokens, reset],
  );

  return <BrandingContext.Provider value={value}>{children}</BrandingContext.Provider>;
}

export function useBranding() {
  const ctx = useContext(BrandingContext);
  if (!ctx) {
    throw new Error("useBranding must be used within a BrandingProvider");
  }
  return ctx;
}
